import React, { useCallback, useMemo, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import { Box, Grid } from "@material-ui/core";

import Loading from "../../../../common/Loading";
import { asCurrency } from "../../../../../utils";
import CustomCardHeader from "../../CustomCardHeader";
import {
  REPORT_ITEM_TYPE,
  REPORT_ITEM_SUB_TYPE
} from "../../../../../constants";
import ContributionDialog from "./ContributionDialog";
import Button from "components/CustomButtons/Button";
import { Close, Edit } from "@material-ui/icons";

const useStyles = makeStyles({
  itemLabel: {
    fontSize: 14
  },
  flexGrow: {
    flexGrow: 1
  },
  actions: {
    margin: "0 0 0 5px",
    padding: 5,
    "& svg": {
      margin: 0
    }
  }
});

const Contributions = ({
  reportID,
  data,
  chapterID,
  year,
  createReportItem,
  updateReportItem,
  initialReportItems,
  removeReportItem,
  onRefetch,
  onHelp,
  onRedirect,
  isEditable
}) => {
  const classes = useStyles();

  const [activeData, setActiveData] = useState({
    item: {},
    isCreating: true
  });
  const [dialogOpen, setDialogOpen] = useState(false);

  const [isDirty, setDirty] = useState(false);

  const items = useMemo(
    () =>
      data.filter(
        item => item.reportItemSubTypeID === REPORT_ITEM_SUB_TYPE.CONTRIBUTIONS
      ),
    [data]
  );

  const onCreate = useCallback(
    async item => {
      const { description, source, amount, grantNo, grantCode } = item;

      let body = {
        description,
        source,
        grantNo,
        grantCode,
        amount: +amount,
        reportItemSubTypeID: REPORT_ITEM_SUB_TYPE.CONTRIBUTIONS,
        reportItemTypeID: REPORT_ITEM_TYPE.EXPENSE
      };

      if (reportID === "add") {
        const _reportID = await initialReportItems({
          chapterID,
          year,
          items: [body]
        });
        onRedirect(_reportID);
      } else {
        await createReportItem({ ...body, reportID });
      }

      onRefetch();
      setDialogOpen(false);
    },
    [reportID, activeData, chapterID]
  );

  const onUpdate = useCallback(
    async item => {
      const { description, source, amount, grantNo, grantCode } = item;

      let body = {
        description,
        source,
        grantNo,
        grantCode,
        amount: +amount,
        reportItemSubTypeID: REPORT_ITEM_SUB_TYPE.CONTRIBUTIONS,
        reportItemTypeID: REPORT_ITEM_TYPE.EXPENSE
      };

      if (reportID === "add") {
        const _reportID = await initialReportItems({
          chapterID,
          year,
          items: [body]
        });
        onRedirect(_reportID);
      } else {
        await updateReportItem(activeData.item.id, { ...body, reportID });
      }

      onRefetch();
      setDialogOpen(false);
    },
    [reportID, activeData]
  );

  const onRemove = useCallback(async item => {
    setDirty(true);
    await removeReportItem(item.id);
    onRefetch();
  }, []);

  const openCreateDialog = useCallback(() => {
    setActiveData({
      item: {},
      isCreating: true
    });
    setDialogOpen(true);
  }, []);

  const openUpdateDialog = useCallback(item => {
    setActiveData({
      item,
      isCreating: false
    });
    setDialogOpen(true);
  }, []);

  return (
    <Box mb={3}>
      <CustomCardHeader
        label={"Contributions"}
        onHelp={() =>
          onHelp(REPORT_ITEM_TYPE.EXPENSE, REPORT_ITEM_SUB_TYPE.CONTRIBUTIONS)
        }
      />
      {items.length !== 0 && (
        <Box px={1} py={0.5}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={4}>
              <Typography>
                <strong>Recipient</strong>
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography>
                <strong>Purpose</strong>
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography align={isEditable ? "left" : "right"}>
                <strong>Amount</strong>
              </Typography>
            </Grid>
          </Grid>
        </Box>
      )}
      <Box display="flex" flexDirection="column">
        {isDirty ? (
          <div style={{ display: "flex", justifyContent: "center" }}>
            <Loading />
          </div>
        ) : (
          <>
            {" "}
            {items.map((item, idx) => (
              <Box px={1} py={0.5} key={`contribution-${idx}`} flex="1">
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={4}>
                    <Typography className={classes.itemLabel}>
                      {item.source}
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography className={classes.itemLabel}>
                      {item.description}
                    </Typography>
                  </Grid>
                  <Grid item className={classes.flexGrow}>
                    <Typography className={classes.itemLabel} align="right">
                      {asCurrency(item.amount || 0)}
                    </Typography>
                  </Grid>
                  {isEditable && (
                    <Grid item>
                      <Box display="flex">
                        <Button
                          simple
                          color="success"
                          className={classes.actions}
                          onClick={() => openUpdateDialog(item, false)}
                        >
                          <Edit />
                        </Button>
                        <Button
                          simple
                          color="danger"
                          onClick={() => onRemove(item)}
                          className={classes.actions}
                        >
                          <Close />
                        </Button>
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </Box>
            ))}
          </>
        )}
      </Box>
      {isEditable && (
        <Box ml={3} mt={2}>
          <a onClick={openCreateDialog}>
            <strong>Add a Contribution</strong>
          </a>
        </Box>
      )}

      <ContributionDialog
        open={dialogOpen}
        data={activeData}
        onClose={() => setDialogOpen(false)}
        onCreate={onCreate}
        onUpdate={onUpdate}
      />
    </Box>
  );
};

export default Contributions;
